import { Maatregel, VsmVergelijking } from "@/entities/vsm";
import { useOudeMaatregelen, useVsmActieDialogContext } from "@/features/acties/components/dialogs/vsm-actie-dialog";
import { MaatregelProvider, useVsmEditorContext, useVsmEditorMaatregelen, VsmMaatregelList, VsmSectionTitle } from "@/features/vsm";
import { Badge, Card } from "@/shared/components";
import { convertToKebabCase } from "@/shared/utils";
import { useMemo } from "react";

export function OudeVsm() {
    const { oudeToelichting, oudeStatus } = useVsmActieDialogContext();

    return (
        <Card className="bg-elevated-sunken gap-4 p-4">
            <div className="mb-4 grid gap-1">
                <h5 className="text-m leading-m font-medium text-inherit">Status</h5>
                <Badge className="mr-auto" variant="gray-subtle" data-testid="vsm-status">
                    {oudeStatus}
                </Badge>
            </div>
            <OudeVsmSection type="RijdtNiet" />
            <OudeVsmSection type="Rijdt" />
            <OudeVsmSection type="Aanvullend" />
            {oudeToelichting?.trim() && (
                <div className="grid gap-1">
                    <h5 className="text-m leading-m font-medium text-inherit">Toelichting VSM</h5>
                    <span data-testid="toelichting">{oudeToelichting}</span>
                </div>
            )}
        </Card>
    );
}

type OudeVsmSectionProps = {
    type: "Rijdt" | "RijdtNiet" | "Aanvullend";
};

function OudeVsmSection({ type }: Readonly<OudeVsmSectionProps>) {
    const oudeMaatregelen = useOudeMaatregelen();
    const nieuweMaatregelen = useVsmEditorMaatregelen();
    const { copyToNieuweVersie, isCopiedToNieuweVersie } = useVsmEditorContext();

    // Which treinseries are merged into the RijdtGroep depends on de nieuwe versie, which the user edits.
    const maatregelen = useMemo(
        () => Maatregel.sectionWithRijdtGroep(oudeMaatregelen, type, VsmVergelijking.belongsInRijdtGroep(nieuweMaatregelen)),
        [nieuweMaatregelen, oudeMaatregelen, type]
    );

    return (
        <div className="flex w-full flex-col gap-2" data-testid={`vsm-section-${convertToKebabCase(type)}`}>
            <div className="flex h-8 flex-row items-center">
                <VsmSectionTitle headerLevel="h5" type={type} />
            </div>
            <MaatregelProvider variant="oud" copyToNieuweVersie={copyToNieuweVersie} isCopiedToNieuweVersie={isCopiedToNieuweVersie}>
                <VsmMaatregelList maatregelen={maatregelen} isSectionEmpty={maatregelen.length === 0} />
            </MaatregelProvider>
        </div>
    );
}
